import {FormControl} from 'platform/components';

import {useCallback, useMemo} from 'react';
import {useWatch} from 'react-hook-form';

import {isNil, reject} from 'ramda';
import {isNilOrEmpty, isTrue} from 'ramda-adjunct';

import {usePostTireSetActionsQuery} from '@dms/api/core';

import {Nullish} from 'shared/web';

import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';

interface UseTireSetActionsParams {
  tireOrderId: string;
  tireSetId: string;
  control: FormControl<TireSetDetailFormDefinition>;
  includedKeys?: string[];
  excludedKeys?: string[];
  homeBranchId: string | Nullish;
}

export function useTireSetActions(params: UseTireSetActionsParams) {
  const {tireOrderId, tireSetId, control, includedKeys, excludedKeys = [], homeBranchId} = params;

  const watchedWheels = useWatch({
    control,
    name: 'wheels',
  });
  const isTireSetRequestEnabled = useWatch({
    control,
    name: 'tireSetRequestEnabled',
  });
  const destinationBranchId = useWatch({
    control,
    name: 'destinationBranchId',
  });

  const getCheckedWheelIds = useCallback(
    () =>
      reject(
        isNil,
        (watchedWheels || []).filter((wheel) => isTrue(wheel.checked)).map((wheel) => wheel.id)
      ),
    [watchedWheels]
  );

  const {
    data: actionsData,
    isError,
    isFetching,
    refetch,
  } = usePostTireSetActionsQuery(
    {
      orderId: tireOrderId,
      setId: tireSetId,
      body: {
        wheelIds: getCheckedWheelIds(),
      },
    },
    {
      skip: isNilOrEmpty(tireSetId) || isNilOrEmpty(getCheckedWheelIds()),
      refetchOnMountOrArgChange: true,
    }
  );

  const availableActions = useMemo(() => {
    if (!actionsData?.options || isError || isNilOrEmpty(getCheckedWheelIds())) {
      return [];
    }

    return actionsData.options
      .filter(
        (option: {type: string; style: string; key: string; position: number}) =>
          option.type === 'list-item'
      )
      .filter(
        (option: {type: string; style: string; key: string; position: number}) =>
          option.style === 'enable'
      )
      .filter((option: {type: string; style: string; key: string; position: number}) => {
        if (includedKeys && includedKeys.length > 0) {
          return includedKeys.includes(option.key);
        }
        if (excludedKeys && excludedKeys.length > 0) {
          return !excludedKeys.includes(option.key);
        }
        return true;
      })
      .sort(
        (
          a: {type: string; style: string; key: string; position: number},
          b: {type: string; style: string; key: string; position: number}
        ) => a.position - b.position
      )
      .filter((option: {type: string; style: string; key: string; position: number}) => {
        if (option.key === 'archive') {
          return !isTireSetRequestEnabled;
        }
        if (option.key === 'issue') {
          return isTireSetRequestEnabled ? homeBranchId === destinationBranchId : true;
        }
        return true;
      });
  }, [
    actionsData,
    isError,
    includedKeys,
    excludedKeys,
    isTireSetRequestEnabled,
    homeBranchId,
    destinationBranchId,
    getCheckedWheelIds,
  ]);

  const refreshActions = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    availableActions,
    getCheckedWheelIds,
    actionsData,
    isFetching,
    refreshActions,
  };
}
